"use client";
import { useEffect, useState } from "react";
import {
  Modal,
  Image,
  Text,
  Button,
  Stack,
  Group,
  Box,
  Paper,
  Anchor,
  SegmentedControl,
  useMantineTheme,
} from "@mantine/core";
import { Carousel, type Embla } from "@mantine/carousel";
import { useElementSize } from "@mantine/hooks";
import {
  IconDownload,
  IconShare,
  IconPlayerPlayFilled,
} from "@tabler/icons-react";
import { getLatestHighlightsEntry } from "@/app/_firebase/changeLog";
import { toEmbedUrl } from "@/app/_helpers/video";
import { ChangeLogHighlight } from "@/app/@types/changeLog";
import { useUserContext } from "@/app/_hooks/useUserContext";
import { updateUserModalFlags } from "@/app/_firebase/user";
import InstagramPost, {
  IG_DIMENSIONS,
} from "@/app/_components/SocialShareAsset/InstagramPost";
import FacebookPost, {
  FB_DIMENSIONS,
} from "@/app/_components/SocialShareAsset/FacebookPost";
import StoryPost, {
  STORY_DIMENSIONS,
} from "@/app/_components/SocialShareAsset/StoryPost";

const STORAGE_KEY = "mm-last-seen-changelog";
const REFERRAL_PROMO_KEY = "mm-referral-promo-1";

const WhatsNewModal = () => {
  const user = useUserContext();
  const [opened, setOpened] = useState(false);
  const [isReferralPromo, setIsReferralPromo] = useState(false);
  const [highlights, setHighlights] = useState<ChangeLogHighlight[]>([]);
  const [activeSlide, setActiveSlide] = useState(0);
  const [embla, setEmbla] = useState<Embla | null>(null);
  const theme = useMantineTheme();

  useEffect(() => {
    if (embla) {
      embla.on("select", () => {
        setActiveSlide(embla.selectedScrollSnap());
      });
    }
  }, [embla]);

  useEffect(() => {
    // Brand-new users go straight into the guided tour. Suppress the What's New
    // and referral modals (they'd otherwise stack on top of the Joyride) and
    // mark them as already seen so they don't pop the moment the tour ends — to
    // a new user everything is already new.
    if (user.walkThroughCompleted === false) {
      getLatestHighlightsEntry()
        .then((entry) => {
          if (entry?.id) localStorage.setItem(STORAGE_KEY, entry.id);
        })
        .catch((err) =>
          console.error("Failed to sync changelog for new user", err)
        );
      return;
    }

    // Referral promo: switched off. It only remembered itself in
    // localStorage, which phones lose (Safari's 7-day purge, in-app
    // browsers), so it kept coming back. Re-enable by restoring this block.
    // const seenPromo = localStorage.getItem(REFERRAL_PROMO_KEY);
    // if (!seenPromo) {
    //   localStorage.setItem(REFERRAL_PROMO_KEY, "seen");
    //   setIsReferralPromo(true);
    //   setOpened(true);
    //   return;
    // }

    // Otherwise check changelog
    const check = async () => {
      try {
        const entry = await getLatestHighlightsEntry();
        if (!entry || !entry.highlights || entry.highlights.length === 0)
          return;

        if (
          user.lastSeenChangelogId === entry.id ||
          localStorage.getItem(STORAGE_KEY) === entry.id
        )
          return;

        // Seen once shown, on the account and in this browser. Phones lose
        // localStorage (Safari's 7-day purge, in-app browsers), so the
        // account copy is what keeps it from coming back.
        localStorage.setItem(STORAGE_KEY, entry.id);
        updateUserModalFlags(user.uid, { lastSeenChangelogId: entry.id }).catch(
          () => {}
        );
        setHighlights(entry.highlights);
        setOpened(true);
      } catch (err) {
        console.error("Failed to check for changelog highlights", err);
      }
    };
    check();
    // lastSeenChangelogId is read once on purpose: our own write flips it,
    // and re-running on that would re-check behind the open modal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.walkThroughCompleted, user.uid]);

  const handleClose = () => {
    setOpened(false);
  };

  if (isReferralPromo) {
    return (
      <Modal
        opened={opened}
        onClose={handleClose}
        centered
        size="xl"
        overlayProps={{ backgroundOpacity: 0.8, blur: 3 }}
        withCloseButton
        trapFocus={false}
      >
        <ReferralPromo onClose={handleClose} />
      </Modal>
    );
  }

  if (highlights.length === 0) return null;

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Text fw={700} size="xl">
          New Update
        </Text>
      }
      centered
      size="auto"
      overlayProps={{ backgroundOpacity: 0.8, blur: 3 }}
      styles={{
        body: {
          padding: 0,
          maxHeight: "92vh",
          overflowY: "auto",
        },
        content: {
          maxWidth: "min(1200px, 90vw)",
        },
      }}
    >
      <Stack gap={0}>
        <Carousel
          getEmblaApi={setEmbla}
          loop
          withControls={false}
          draggable={false}
          style={{ width: "100%" }}
          slideSize={"100%"}
          slidesToScroll={1}
          slideGap={15}
        >
          {highlights.map((h, i) => (
            <Carousel.Slide key={i}>
              <Stack gap="md" align="center" px="lg" pt="sm" pb="md">
                {h.videoUrl && h.image ? (
                  <Paper
                    component="a"
                    href={h.videoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    shadow="sm"
                    radius="md"
                    withBorder
                    style={{
                      overflow: "hidden",
                      position: "relative",
                      cursor: "pointer",
                      display: "block",
                    }}
                  >
                    <Image
                      src={h.image}
                      alt={h.caption || `Slide ${i + 1}`}
                      w="100%"
                      fit="contain"
                      style={{ maxHeight: "68vh" }}
                      radius="md"
                    />
                    <Box
                      style={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "rgba(0,0,0,0.25)",
                      }}
                    >
                      <Box
                        style={{
                          width: 72,
                          height: 72,
                          borderRadius: "50%",
                          background: "rgba(255,255,255,0.95)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          boxShadow: "0 4px 16px rgba(0,0,0,0.3)",
                        }}
                      >
                        <IconPlayerPlayFilled
                          size={32}
                          color={theme.colors.blue[6]}
                        />
                      </Box>
                    </Box>
                    {h.caption && (
                      <Box
                        style={{
                          position: "absolute",
                          bottom: 0,
                          left: 0,
                          right: 0,
                          padding: "56px 24px 18px",
                          background:
                            "linear-gradient(to top, rgba(17,17,17,0.9) 0%, rgba(17,17,17,0.8) 30%, rgba(17,17,17,0.5) 55%, rgba(17,17,17,0.22) 75%, rgba(17,17,17,0.06) 90%, rgba(17,17,17,0) 100%)",
                        }}
                      >
                        <Text
                          ta="center"
                          fw={400}
                          size="lg"
                          c="white"
                          style={{ textShadow: "0 1px 2px rgba(0,0,0,0.5)" }}
                        >
                          {h.caption}
                        </Text>
                      </Box>
                    )}
                  </Paper>
                ) : h.videoUrl ? (
                  <Paper
                    shadow="sm"
                    radius="md"
                    withBorder
                    style={{
                      overflow: "hidden",
                      // The modal is size="auto", so a percentage width would
                      // collapse. Size the player explicitly and let the modal
                      // grow around it. The 260px allows for the modal header,
                      // caption and footer so the whole thing fits without
                      // scrolling.
                      width: "min(1100px, 86vw, calc((100vh - 260px) * 16 / 9))",
                      aspectRatio: "16 / 9",
                    }}
                  >
                    <iframe
                      src={toEmbedUrl(h.videoUrl)}
                      title={h.caption || `Slide ${i + 1}`}
                      style={{ width: "100%", height: "100%", border: 0 }}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                    />
                  </Paper>
                ) : (
                  h.image && (
                    <Paper
                      shadow="sm"
                      radius="md"
                      withBorder
                      style={{ overflow: "hidden", position: "relative" }}
                    >
                      <Image
                        src={h.image}
                        alt={h.caption || `Slide ${i + 1}`}
                        w="100%"
                        fit="contain"
                        style={{ maxHeight: "68vh" }}
                        radius="md"
                      />
                      {h.caption && (
                        <Box
                          style={{
                            position: "absolute",
                            bottom: 0,
                            left: 0,
                            right: 0,
                            padding: "56px 24px 18px",
                            background:
                              "linear-gradient(to top, rgba(17,17,17,0.9) 0%, rgba(17,17,17,0.8) 30%, rgba(17,17,17,0.5) 55%, rgba(17,17,17,0.22) 75%, rgba(17,17,17,0.06) 90%, rgba(17,17,17,0) 100%)",
                          }}
                        >
                          <Text
                            ta="center"
                            fw={400}
                            size="lg"
                            c="white"
                            style={{ textShadow: "0 1px 2px rgba(0,0,0,0.5)" }}
                          >
                            {h.caption}
                          </Text>
                        </Box>
                      )}
                    </Paper>
                  )
                )}
              </Stack>
            </Carousel.Slide>
          ))}
        </Carousel>

        {highlights.length > 1 && (
          <Group justify="center" gap={6} pb="sm">
            {highlights.map((_, i) => (
              <Box
                key={i}
                onClick={() => embla?.scrollTo(i)}
                style={{
                  width: activeSlide === i ? 24 : 8,
                  height: 8,
                  borderRadius: 4,
                  backgroundColor:
                    activeSlide === i
                      ? theme.colors.blue[6]
                      : theme.colors.gray[3],
                  cursor: "pointer",
                  transition: "all 200ms ease",
                }}
              />
            ))}
          </Group>
        )}

        <Group
          justify="space-between"
          px="lg"
          pb="lg"
          pt="xs"
          style={{
            position: "sticky",
            bottom: 0,
            backgroundColor: "var(--mantine-color-body)",
            borderTop: "1px solid var(--mantine-color-gray-2)",
          }}
        >
          <Anchor href="/changes" size="sm" onClick={handleClose}>
            View full changelog
          </Anchor>
          <Group gap="xs">
            {activeSlide > 0 && (
              <Button
                variant="subtle"
                size="sm"
                onClick={() => embla?.scrollPrev()}
              >
                Back
              </Button>
            )}
            {activeSlide < highlights.length - 1 ? (
              <Button size="sm" onClick={() => embla?.scrollNext()}>
                Next
              </Button>
            ) : (
              <Button size="sm" onClick={handleClose}>
                Got it
              </Button>
            )}
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
};

type Platform = "instagram" | "facebook" | "story";

const PROMO_DIMENSIONS: Record<Platform, { width: number; height: number }> = {
  instagram: IG_DIMENSIONS,
  facebook: FB_DIMENSIONS,
  story: STORY_DIMENSIONS,
};

const MAX_SCALES: Record<Platform, number> = {
  instagram: 0.45,
  facebook: 0.65,
  story: 0.35,
};

function ReferralPromo({ onClose }: { onClose: () => void }) {
  const user = useUserContext();
  const [platform, setPlatform] = useState<Platform>("instagram");
  const { ref: containerRef, width: containerWidth } = useElementSize();
  const [logoDataUrl, setLogoDataUrl] = useState<string | null>(null);
  const [logoReady, setLogoReady] = useState(!user.settings.logoSrc);

  useEffect(() => {
    if (!user.settings.logoSrc) return;
    const img = new window.Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        canvas.getContext("2d")?.drawImage(img, 0, 0);
        setLogoDataUrl(canvas.toDataURL("image/png"));
      } catch {
        setLogoDataUrl(user.settings.logoSrc || null);
      }
      setLogoReady(true);
    };
    img.onerror = () => {
      setLogoDataUrl(user.settings.logoSrc || null);
      setLogoReady(true);
    };
    img.src = user.settings.logoSrc;
  }, [user.settings.logoSrc]);

  const referralUrl = `https://www.mountmonitor.com?utm_source=${
    user.settings.slug || user.uid
  }`;

  const generateImage = async (): Promise<Blob | null> => {
    const element = document.getElementById("social-share-asset");
    if (!element) return null;
    const { toBlob } = await import("html-to-image");
    return toBlob(element, { pixelRatio: 1, backgroundColor: "#0099f6" });
  };

  const shareAsset = async () => {
    try {
      const blob = await generateImage();
      if (!blob) return;
      const file = new File([blob], `mountmonitor-referral-${platform}.png`, {
        type: "image/png",
      });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({
          files: [file],
          text: `We run on @mountmonitor — the taxidermy software that runs our shop and keeps our clients coming back. Get a FREE month on us 🦌 #taxidermy #mountmonitor`,
        });
        return;
      }
      await downloadAsset();
    } catch (error: any) {
      if (error?.name !== "AbortError") console.error("Share error:", error);
    }
  };

  const downloadAsset = async () => {
    try {
      const blob = await generateImage();
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.download = `mountmonitor-referral-${platform}.png`;
      link.href = url;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download error:", error);
    }
  };

  const { width: assetWidth } = PROMO_DIMENSIONS[platform];
  const maxScale = MAX_SCALES[platform];
  const scale = containerWidth
    ? Math.min(maxScale, (containerWidth - 32) / assetWidth)
    : maxScale;

  const assetProps = {
    logoSrc: logoDataUrl || user.settings.logoSrc,
    businessName: user.settings.name,
    referralUrl,
    scale,
  };

  return (
    <Stack gap="md" align="center" ref={containerRef}>
      <Stack gap={4} align="center">
        <Text fw={800} size="32px" ta="center">
          Get free months
        </Text>
        <Text ta="center" c="dimmed" size="sm" maw="600px">
          Every shop that joins helps us build better tools for everyone. Share
          your link, they get a free month and so do you.
        </Text>
      </Stack>

      <SegmentedControl
        value={platform}
        onChange={(v) => setPlatform(v as Platform)}
        size="xs"
        data={[
          { label: "Instagram", value: "instagram" },
          { label: "Facebook", value: "facebook" },
          { label: "Story", value: "story" },
        ]}
      />

      {logoReady && (
        <>
          {platform === "instagram" && <InstagramPost {...assetProps} />}
          {platform === "facebook" && <FacebookPost {...assetProps} />}
          {platform === "story" && <StoryPost {...assetProps} />}
        </>
      )}

      <Group gap="xs">
        <Button
          leftSection={<IconDownload size={16} />}
          variant="light"
          onClick={downloadAsset}
          size="sm"
        >
          Download
        </Button>
        <Button
          leftSection={<IconShare size={16} />}
          onClick={shareAsset}
          size="sm"
        >
          Share
        </Button>
      </Group>

      <Button variant="subtle" size="xs" c="dimmed" onClick={onClose}>
        Do later in settings
      </Button>
    </Stack>
  );
}

export default WhatsNewModal;
